import PropTypes from "prop-types";
import React, { useCallback, useEffect, useState } from "react";
import { authenticatedHeadersHash } from "views_shared/app/javascript/scripts/authentication";
import { Tab, Tabs } from "react-bootstrap";

import CodeEditor from "./CodeEditor";
import TopPrTable from "./TopPrTable";
import AddPersonModal from "./AddPersonModal";
import cleanupQwotedTopHundredEntry from "./DataCleanup";

// Constants for API URLs
const topPrsUrl = "/interact/api/promoter_analytics?period=:period&limit=160&prs_only=true&order=desc";
const entryUrl = "/admin/qwoted_top_hundreds/:id.json";
const csrfToken = document.querySelector("meta[name='csrf-token']")?.getAttribute("content");

window.DI__QwotedTopHundred = {
  fetchQwotedTopHundred: async (period) => {
    const response = await fetch(topPrsUrl.replace(":period", period), {
      cache: "no-store",
      method: "GET",
      headers: new Headers(authenticatedHeadersHash()),
    });

    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
    return response.json();
  },

  fetchCurrentQwotedTopHundred: async () => {
    const period = globalThis.Qwoted.BUSINESS_ENVIRONMENT === "development" ? "all_time" : "this_quarter";
    return window.DI__QwotedTopHundred.fetchQwotedTopHundred(period);
  },

  fetchPreviousQwotedTopHundred: async () => {
    // return window.DI__QwotedTopHundred.fetchQwotedTopHundred("all_time"); // useful for development DONTCOMMIT uncommented
    return window.DI__QwotedTopHundred.fetchQwotedTopHundred("last_quarter");
  },
};

// Hook: Manages the state and operations of a Qwoted Top Hundred entity
// (includes communication with the backend)
function useQwotedTopHundredEntityData(id) {
  const [entity, setEntity] = useState(null); // Current entity data
  const [loading, setLoading] = useState(true); // Loading state
  const [error, setError] = useState(null); // Error state

  const url = entryUrl.replace(":id", id.toString());

  // Fetches the entity data
  const fetchEntity = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const data = await response.json();
      setEntity(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [url]);

  // Saves updates to the entity payload
  const saveEntity = async (updatedPayload) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({ qwoted_top_hundred: { payload: updatedPayload } }),
      });
      if (!response.ok) throw new Error(`Server error: ${response.status}`);
      const updatedEntity = await response.json();
      setEntity(updatedEntity);
    } catch (err) {
      setError(`Failed to save data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Reloads entity payload with new data from top PRs
  // (We accept a "fetchFn" argument so we can fetch either current or last quarter.)
  const reloadEntityFromTopPrs = async (fetchFn) => {
    setLoading(true);
    setError(null);
    try {
      const json = await fetchFn(); // e.g. window.fetchCurrentQwotedTopHundred() or window.fetchPreviousQwotedTopHundred()
      const loadedTopPrs = json.data.promoters.map((pr) => ({
        // Get rid of profile data to reenrich later with up to date info:
        ...cleanupQwotedTopHundredEntry(pr),
        hidden: !!pr.ineligible,
      }));
      await saveEntity(loadedTopPrs);
    } catch (err) {
      setError(`Failed to load new data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Fetches the entity data on component mount or when `id` changes
  useEffect(() => {
    fetchEntity();
  }, [id, fetchEntity]);

  return { entity, loading, error, saveEntity, reloadEntityFromTopPrs };
}

// Main Component: Manages Qwoted Top Hundred entity details and UI
function QwotedTopHundred({ id }) {
  const { entity, loading, error, saveEntity, reloadEntityFromTopPrs } = useQwotedTopHundredEntityData(id);
  const [activeTab, setActiveTab] = useState("visual"); // Active tab state
  const [showAddModal, setShowAddModal] = useState(false); // Add-person modal visibility state

  // Saves changes to the top PRs
  const handleTopPrsChange = async (newTopPrs) => {
    await saveEntity(newTopPrs);
  };

  // Repopulates the payload from either "current" or "previous" quarter
  const handleTopPrsRepopulate = async (fetchFn) => {
    if (
      entity.payload &&
      entity.payload.length > 0 &&
      !window.confirm("This will overwrite the current list. Are you sure?") // eslint-disable-line no-alert
    ) {
      return;
    }

    await reloadEntityFromTopPrs(fetchFn);
  };

  // Toggles the visibility of a row in the top PRs table
  const toggleRowVisibility = (profileId) => {
    const updatedPayload = entity.payload.map((pr) =>
      pr.profile_id === profileId ? { ...pr, hidden: !pr.hidden } : pr,
    );
    saveEntity(updatedPayload);
  };

  // Adds a person to the list, either at a specific position or at the end
  const addItem = async (personData, position) => {
    // Respect the eligibility-derived hidden flag AddPersonModal already computed (e.g. an
    // ineligible person is added pre-hidden) instead of forcing every manual add to visible.
    const newEntry = { ...personData, hidden: personData.hidden ?? false };

    let updatedPayload;
    if (position !== undefined && position > 0 && position <= entity.payload.length + 1) {
      const insertIndex = position - 1;
      updatedPayload = [...entity.payload.slice(0, insertIndex), newEntry, ...entity.payload.slice(insertIndex)];
    } else {
      updatedPayload = [...entity.payload, newEntry];
    }

    await saveEntity(updatedPayload);
  };

  // Gets list of existing profile IDs for duplicate detection
  const existingProfileIds = entity?.payload?.map((entry) => entry.profile_id) || [];

  // Reloads the page
  const pageReload = () => {
    window.location.reload();
  };

  // Displays a loading state if data is not ready (e.g. on the first load)
  if (loading && (!entity || !entity.payload)) {
    return <div className="text-center mt-5">Loading...</div>;
  }

  // Displays an error state with a reload option
  if (error) {
    return (
      <div className="text-center text-danger mt-5">
        <p>Error: {error}</p>
        <p>
          <button type="button" className="btn btn-link" onClick={pageReload}>
            Reload the page
          </button>
        </p>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h1 className="mb-0">
          <a href="/admin/qwoted_top_hundreds">
            <i className="fa-solid fa-arrow-left" />
          </a>
          &nbsp;
          {entity.title}
        </h1>
        <button type="button" className="btn btn-success" onClick={() => setShowAddModal(true)}>
          <i className="fas fa-plus me-1" />
          Add Person
        </button>
      </div>

      <AddPersonModal
        show={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdd={addItem}
        existingProfileIds={existingProfileIds}
        listSize={entity?.payload?.length || 0}
      />

      <div className={loading ? "opacity-25 pe-none" : ""}>
        <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mt-4">
          <Tab eventKey="visual" title="Visual">
            <TopPrTable prs={entity.payload} toggleRowVisibility={toggleRowVisibility} />
          </Tab>
          <Tab eventKey="code" title="Code">
            <div>
              <CodeEditor topPrs={entity.payload} onTopPrsChanged={handleTopPrsChange} />
            </div>
          </Tab>
        </Tabs>
        <div className="mt-3">
          <button type="button" className="btn btn-success me-3" onClick={() => setShowAddModal(true)}>
            <i className="fas fa-plus me-1" />
            Add Person
          </button>
          <button
            type="button"
            className="btn btn-warning me-3"
            onClick={() => handleTopPrsRepopulate(window.DI__QwotedTopHundred.fetchPreviousQwotedTopHundred)}
          >
            Load from previous quarter Qwoted 100
          </button>
          <button
            type="button"
            className="btn btn-warning"
            onClick={() => handleTopPrsRepopulate(window.DI__QwotedTopHundred.fetchCurrentQwotedTopHundred)}
          >
            Load from this quarter Qwoted 100
          </button>
        </div>
      </div>
    </div>
  );
}

QwotedTopHundred.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired, // Entity ID
};

export default QwotedTopHundred;
